import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, X, ChevronRight, ChevronLeft, Sparkles, CheckCircle2, Circle, Loader2, Eye, ArrowUp, ArrowDown, PenLine } from "lucide-react";
import { SourcePreviewDialog, SourceStatusBadge, validateSourceFile, sourceLabel, SUPPORTED_SOURCE_TEXT, SOURCE_ACCEPT } from "@/components/SourceFiles";

const steps = ["Template", "Information", "Details", "Generate", "Review", "Export"];

export default function NewDocument() {
  const { activeWorkspace } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialMode = params.get("mode") === "standardise" ? "standardise" : "create";

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(initialMode);
  const [treatment, setTreatment] = useState("structure");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ title:"", document_number:"", revision:"0", department:"", site:"", author:"", reviewer:"", approver:"", effective_date:"", custom_instructions:"" });
  const [customFields, setCustomFields] = useState({});
  const [jobId, setJobId] = useState(null);
  const [docId, setDocId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!activeWorkspace) return;
    api.get(`/workspaces/${activeWorkspace.id}/templates`).then((r) => setTemplates(r.data.filter(t=>t.status==="active")));
  }, [activeWorkspace]);

  const chosenTemplate = useMemo(() => templates.find(t=>t.id===templateId), [templates, templateId]);

  const doUpload = async (fileList) => {
    setUploading(true);
    for (const f of fileList) {
      const err = validateSourceFile(f);
      if (err) { toast.error(err); continue; }
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post(`/workspaces/${activeWorkspace.id}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => (prev.some((p) => p.id === data.id) ? prev : [...prev, data]));
        if (data.duplicate) toast.warning(data.duplicate_message);
        else if (data.extraction_status === "unreadable") toast.warning(`${f.name}: ${data.extraction_note}`);
        else if (data.extraction_status === "unsupported") toast.info(`${f.name}: ${data.extraction_note}`);
      } catch (e) { toast.error(e?.response?.data?.detail || `Could not upload ${f.name}`); }
    }
    setUploading(false);
  };
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));
  const moveFile = (idx, dir) => setFiles((prev) => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const renameFile = async (f) => {
    const label = window.prompt("Display label for this source file", sourceLabel(f));
    if (!label || label === sourceLabel(f)) return;
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}/uploads/${f.id}`, { display_label: label });
      setFiles((prev) => prev.map((p) => (p.id === f.id ? { ...p, display_label: label } : p)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename file"); }
  };

  const startGenerate = async () => {
    if (!templateId) { toast.error("Choose a template"); return; }
    if (!meta.title) { toast.error("Enter a document title"); return; }
    // validate required custom fields
    for (const f of (chosenTemplate?.metadata_fields || [])) {
      if (f.required) {
        const v = customFields[f.key];
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
          toast.error(`${f.label} is required`); return;
        }
      }
    }
    setStep(3);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/documents/generate`, {
        template_id: templateId, mode, treatment, ...meta, notes, source_file_ids: files.map(f=>f.id), custom_fields: customFields,
      });
      setJobId(data.job_id); setDocId(data.document_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
      setStep(2);
    }
  };

  useEffect(() => {
    if (!jobId || !activeWorkspace) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspace.id}/generation-jobs/${jobId}`);
        setJobStatus(data.status);
        if (data.status === "completed") { clearInterval(t); setStep(4); }
        if (data.status === "failed") { clearInterval(t); toast.error(data.error_message || "Generation failed"); setStep(2); }
      } catch { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
  }, [jobId, activeWorkspace]);

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#1E293B] bg-[#070B18] px-6 py-4 sticky top-0 z-20" data-testid="stepper">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${i === step ? "bg-gradient-to-br from-[#1478FF] to-[#725BFF] text-white fx-glow-cyan" : i < step ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/40" : "bg-[#0D1224] text-slate-500 border border-slate-800"}`}>{i < step ? <CheckCircle2 size={14}/> : i+1}</div>
              <div className={`text-xs font-medium hidden sm:block ${i === step ? "text-white" : i < step ? "text-cyan-300" : "text-slate-500"}`}>{s}</div>
              {i < steps.length -1 && <div className={`flex-1 h-px ${i < step ? "bg-cyan-500/40" : "bg-slate-800"}`} />}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8">
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">What are you creating?</h2>
              <p className="text-sm text-slate-400 mt-1">Choose the workflow that fits your task.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <ModeCard active={mode==="create"} onClick={()=>setMode("create")} title="Create new document" body="Turn notes, files and information into a company-standard document." testid="mode-create"/>
              <ModeCard active={mode==="standardise"} onClick={()=>setMode("standardise")} title="Standardise existing document" body="Bring an existing document into the current company template and standards." testid="mode-standardise"/>
            </div>
            {mode === "standardise" && (
              <div className="fx-panel p-4">
                <div className="text-sm text-slate-300 mb-3">Treatment</div>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    ["formatting","Formatting only","Keep wording mainly unchanged."],
                    ["structure","Formatting + structure","Reorganise into target structure."],
                    ["full","Full standardisation","Improve structure and clarity."],
                  ].map(([k,l,d]) => (
                    <button key={k} onClick={()=>setTreatment(k)} className={`text-left p-3 rounded-lg border ${treatment===k ? "border-cyan-400/50 bg-cyan-500/5" : "border-[#1E293B] hover:border-slate-600"}`} data-testid={`treatment-${k}`}>
                      <div className="text-sm text-slate-100 font-medium">{l}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{d}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-sm text-slate-300 mb-3">Choose a template</div>
              <div className="grid md:grid-cols-2 gap-3">
                {templates.map((t) => (
                  <button key={t.id} onClick={()=>setTemplateId(t.id)} className={`text-left p-4 rounded-lg border transition ${templateId===t.id ? "border-cyan-400/60 bg-cyan-500/5" : "border-[#1E293B] hover:border-slate-600 bg-[#0D1224]"}`} data-testid={`template-${t.id}`}>
                    <div className="mono text-[10px] uppercase tracking-widest text-slate-500">{t.category}</div>
                    <div className="text-sm text-slate-100 font-medium mt-1">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-1">v{t.current_version} · {t.sections?.length || 0} sections</div>
                  </button>
                ))}
                {templates.length === 0 && <div className="text-sm text-slate-500 fx-panel p-4">No active templates. <a className="text-cyan-300" href="/app/templates">Add one</a> to continue.</div>}
              </div>
            </div>
            <Nav onNext={() => setStep(1)} nextDisabled={!templateId} testidNext="nd-next-0"/>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">Add your information</h2>
              <p className="text-sm text-slate-400 mt-1">Paste notes and upload supporting files. Fexidoc reads text from PDF, Word, text and CSV files server-side and uses it during generation.</p>
            </div>
            <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={10} placeholder="Paste notes, rough instructions, procedure information or any raw content here." className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg p-3 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="notes-input"/>
            <FileDrop onFiles={doUpload} uploading={uploading}/>
            {files.length > 0 && (
              <div className="space-y-2" data-testid="source-file-list">
                {files.map((f, idx) => (
                  <div key={f.id} className="fx-panel p-3" data-testid={`source-file-${f.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <FileText size={16} className="text-cyan-300 shrink-0 mt-0.5"/>
                        <div className="min-w-0">
                          <div className="text-sm text-slate-100 truncate">{sourceLabel(f)}</div>
                          <div className="text-[11px] text-slate-500 mono">{(f.size/1024).toFixed(1)} KB · {f.file_type}</div>
                          <div className="mt-1"><SourceStatusBadge status={f.extraction_status} testid={`source-status-${f.id}`}/></div>
                          {f.extraction_note && <div className="text-[11px] text-amber-300/80 mt-1 max-w-xl">{f.extraction_note}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={()=>moveFile(idx,-1)} disabled={idx===0} className="text-slate-500 hover:text-slate-200 p-1 disabled:opacity-30" title="Move up" data-testid={`source-up-${f.id}`}><ArrowUp size={14}/></button>
                        <button onClick={()=>moveFile(idx,1)} disabled={idx===files.length-1} className="text-slate-500 hover:text-slate-200 p-1 disabled:opacity-30" title="Move down" data-testid={`source-down-${f.id}`}><ArrowDown size={14}/></button>
                        <button onClick={()=>renameFile(f)} className="text-slate-500 hover:text-cyan-300 p-1" title="Rename label" data-testid={`source-rename-${f.id}`}><PenLine size={14}/></button>
                        <button onClick={()=>setPreview(f)} className="text-slate-500 hover:text-cyan-300 p-1" title="Preview" data-testid={`source-preview-${f.id}`}><Eye size={14}/></button>
                        <button onClick={()=>removeFile(f.id)} className="text-slate-500 hover:text-red-400 p-1" title="Remove from this document" data-testid={`remove-file-${f.id}`}><X size={14}/></button>
                      </div>
                    </div>
                  </div>
                ))}
                {files.some(f=>f.extraction_status!=="ready") && (
                  <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2" data-testid="source-warning">
                    Some files couldn't be read. Their contents won't be used during generation — remove them or add the key details to your notes.
                  </div>
                )}
              </div>
            )}
            <Nav onBack={()=>setStep(0)} onNext={()=>setStep(2)} testidNext="nd-next-1"/>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">Document details</h2>
              <p className="text-sm text-slate-400 mt-1">These appear on the document header and metadata table.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Text label="Document title" value={meta.title} onChange={(v)=>setMeta({...meta, title:v})} testid="meta-title" required/>
              <Text label="Document number (optional)" value={meta.document_number} onChange={(v)=>setMeta({...meta, document_number:v})} placeholder={chosenTemplate?.document_prefix ? `${chosenTemplate.document_prefix}-###` : "auto"} testid="meta-number"/>
              <Text label="Revision" value={meta.revision} onChange={(v)=>setMeta({...meta, revision:v})} testid="meta-revision"/>
              <Text label="Effective date" type="date" value={meta.effective_date} onChange={(v)=>setMeta({...meta, effective_date:v})} testid="meta-date"/>
              <Text label="Department" value={meta.department} onChange={(v)=>setMeta({...meta, department:v})} testid="meta-department"/>
              <Text label="Site" value={meta.site} onChange={(v)=>setMeta({...meta, site:v})} testid="meta-site"/>
              <Text label="Author" value={meta.author} onChange={(v)=>setMeta({...meta, author:v})} testid="meta-author"/>
              <Text label="Reviewer" value={meta.reviewer} onChange={(v)=>setMeta({...meta, reviewer:v})} testid="meta-reviewer"/>
              <Text label="Approver" value={meta.approver} onChange={(v)=>setMeta({...meta, approver:v})} testid="meta-approver"/>
            </div>
            {(chosenTemplate?.metadata_fields || []).length > 0 && (
              <div className="fx-panel p-4 space-y-3" data-testid="custom-fields-panel">
                <div className="mono text-[10px] uppercase tracking-widest text-cyan-300">Template fields · {chosenTemplate.name}</div>
                <div className="grid md:grid-cols-2 gap-4">
                  {chosenTemplate.metadata_fields.map((f) => (
                    <CustomField key={f.key} field={f} value={customFields[f.key]} onChange={(v)=>setCustomFields({...customFields, [f.key]: v})}/>
                  ))}
                </div>
              </div>
            )}
            <label className="block">
              <div className="text-xs text-slate-400 mb-1">Additional instructions (optional)</div>
              <textarea value={meta.custom_instructions} onChange={(e)=>setMeta({...meta, custom_instructions:e.target.value})} rows={3} placeholder="e.g. Use metric units. Reference AS/NZS standards where relevant." className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg p-3 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="meta-instructions"/>
            </label>
            <Nav onBack={()=>setStep(1)} onNext={startGenerate} nextLabel="Generate document" testidNext="nd-generate"/>
          </div>
        )}

        {step === 3 && (
          <div className="fx-panel p-12 text-center" data-testid="generating-panel">
            <div className="inline-flex items-center gap-3 mono text-xs uppercase tracking-widest text-cyan-300 mb-4"><Loader2 size={14} className="animate-spin"/> Generating</div>
            <h2 className="font-display text-2xl font-bold text-white">Fexidoc is preparing your document</h2>
            <p className="text-sm text-slate-400 mt-2">Reading source material · Mapping to template · Applying workspace standards · Checking required sections</p>
            <div className="mt-8 grid gap-2 max-w-md mx-auto">
              {["Reading source material","Mapping information","Applying template","Applying workspace standards","Checking required sections","Preparing review"].map((s, i) => (
                <div key={s} className="flex items-center gap-2 text-sm text-slate-300"><Circle size={10} className={i < 3 ? "text-cyan-400 fill-cyan-400" : "text-slate-600"}/> {s}</div>
              ))}
            </div>
            <div className="mt-6 text-[11px] mono text-slate-500 uppercase tracking-widest">Job {jobStatus || "queued"}</div>
          </div>
        )}

        {step === 4 && docId && (
          <div className="fx-panel p-10 text-center" data-testid="ready-panel">
            <CheckCircle2 size={40} className="mx-auto text-emerald-400"/>
            <h2 className="font-display text-2xl font-bold text-white mt-3">Ready for review</h2>
            <p className="text-sm text-slate-400 mt-1">Your document has been structured. Review before exporting.</p>
            <div className="mt-6 flex justify-center gap-3">
              <button onClick={()=>nav(`/app/documents/${docId}`)} className="fx-btn-primary text-white px-5 py-2.5 rounded-lg font-medium" data-testid="open-review">Open review</button>
              <button onClick={()=>nav("/app/documents")} className="border border-white/10 hover:border-white/20 text-slate-200 px-5 py-2.5 rounded-lg">Back to library</button>
            </div>
          </div>
        )}
      </div>
      {preview && <SourcePreviewDialog file={preview} onClose={()=>setPreview(null)}/>}
    </div>
  );
}

function ModeCard({active, onClick, title, body, testid}) {
  return (
    <button onClick={onClick} className={`text-left fx-panel p-6 transition group ${active ? "border-cyan-400/60 bg-cyan-500/5" : "hover:border-slate-600"}`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${active ? "bg-cyan-500/20 border-cyan-400/40" : "bg-[#10162A] border-[#1E293B]"}`}><Sparkles size={16} className={active ? "text-cyan-300" : "text-slate-400"}/></div>
      <div className="font-display text-lg font-semibold text-white mt-4">{title}</div>
      <div className="text-sm text-slate-400 mt-1 leading-relaxed">{body}</div>
    </button>
  );
}

function FileDrop({ onFiles, uploading }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={(e)=>{e.preventDefault(); setDrag(false); onFiles(Array.from(e.dataTransfer.files));}}
      className={`block border-2 border-dashed rounded-xl p-10 text-center transition cursor-pointer ${drag ? "border-cyan-400 bg-cyan-500/5" : "border-[#1E293B] hover:border-slate-600"}`}
      data-testid="file-drop"
    >
      <UploadCloud size={22} className="mx-auto text-cyan-300"/>
      <div className="mt-3 text-sm text-slate-200">{uploading ? "Uploading…" : "Drag files here or click to upload"}</div>
      <div className="text-[11px] text-slate-500 mt-1">{SUPPORTED_SOURCE_TEXT}</div>
      <input type="file" multiple accept={SOURCE_ACCEPT} className="hidden" onChange={(e)=>onFiles(Array.from(e.target.files))} data-testid="file-input"/>
    </label>
  );
}

function Text({label, value, onChange, type="text", placeholder, required, testid}) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}{required && <span className="text-red-400 ml-1">*</span>}</div>
      <input type={type} value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/>
    </label>
  );
}

function Nav({onBack, onNext, nextLabel="Continue", nextDisabled, testidNext}) {
  return (
    <div className="flex justify-between items-center pt-4 border-t border-[#1E293B]">
      {onBack ? <button onClick={onBack} className="text-slate-400 hover:text-white inline-flex items-center gap-1 px-2 py-1"><ChevronLeft size={16}/> Back</button> : <div/>}
      <button onClick={onNext} disabled={nextDisabled} className="fx-btn-primary text-white font-medium px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid={testidNext}>{nextLabel} <ChevronRight size={16}/></button>
    </div>
  );
}


function CustomField({ field, value, onChange }) {
  const label = <div className="text-xs text-slate-400 mb-1">{field.label}{field.required && <span className="text-red-400 ml-1">*</span>}{field.help && <span className="ml-2 text-slate-500 font-normal">— {field.help}</span>}</div>;
  const cls = "w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none";
  const testid = `custom-${field.key}`;
  if (field.type === "long_text") return <label className="block md:col-span-2">{label}<textarea rows={3} value={value || ""} onChange={(e)=>onChange(e.target.value)} className={cls} data-testid={testid}/></label>;
  if (field.type === "number") return <label className="block">{label}<input type="number" value={value ?? ""} onChange={(e)=>onChange(e.target.value === "" ? "" : Number(e.target.value))} className={cls} data-testid={testid}/></label>;
  if (field.type === "date") return <label className="block">{label}<input type="date" value={value || ""} onChange={(e)=>onChange(e.target.value)} className={cls} data-testid={testid}/></label>;
  if (field.type === "toggle") return <label className="flex items-center gap-2 pt-6">{label}<input type="checkbox" checked={!!value} onChange={(e)=>onChange(e.target.checked)} className="accent-cyan-400" data-testid={testid}/></label>;
  if (field.type === "dropdown") return <label className="block">{label}<select value={value || ""} onChange={(e)=>onChange(e.target.value)} className={cls} data-testid={testid}><option value="">Select…</option>{(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}</select></label>;
  if (field.type === "person") return <label className="block">{label}<input value={value || ""} onChange={(e)=>onChange(e.target.value)} placeholder="Name or email" className={cls} data-testid={testid}/></label>;
  return <label className="block">{label}<input value={value || ""} onChange={(e)=>onChange(e.target.value)} className={cls} data-testid={testid}/></label>;
}
